import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./style.scss";
import Modal from "components/Common/Modal";
import { SwitchTab } from "features/Tab";
import { useIpAuthFlg } from "hooks/useIpAuthFlg";
import { CorpName } from "./Tab/CorpName";
import { Industry } from "./Tab/Industry";
import { Collection } from "./Tab/Collection";
import { Recent } from "./Tab/Recent";
import Button from "components/Common/Button/Text";
import { SelectedItemTable } from "./SelectedItemTable";
import { UpperLimitModal } from "./UpperLimitModal";
import { CloseConfirmModal } from "features/BinderPage/Common/CloseConfirmModal";
import { Corp, MstL, MstM, MstS, SourceItem } from "models/CorpScreening";
import { trackAction } from "utils/helper/analytics";
import { findAnalyticsWithPath } from "utils/helper/url";

export interface Item {
  companyCode: string;
  companyName: string;
  stkCode: string;
  source: string;
  companyKbn?: string;
  baseOptFlg?: string;
}

interface SwitchTabItem {
  text: string;
  key: string;
  onSwitch?: () => void;
  count?: number;
}

interface SearchList {
  searchResult: Item[] | null;
  searchCorp: ({
    companyName,
    suggestFlag,
    suggestMeta,
    corpIndustyKbn
  }: {
    companyName: string;
    suggestFlag?: boolean;
    suggestMeta?: string | undefined;
    corpIndustyKbn?: string | undefined;
  }) => Promise<void>;
  type: string;
  isLoading: boolean;
  ignoreInit?: boolean;
  defaultKeyword?: string;
  disabledCodes?: string[];
}

interface SelectMenu {
  corpList: Corp[];
  getNeedsMstL: () => Promise<void>;
  getNeedsMstM: ({ nkgncodel }: { nkgncodel: string }) => Promise<void>;
  getNeedsMstS: ({
    nkgncodel,
    nkgncodem
  }: {
    nkgncodel: string;
    nkgncodem: string;
  }) => Promise<void>;
  onClickNeedsMstS: (params: {
    allChecked: boolean;
    concatenatedCorpFilterValue: string;
    corpIndustyCode: string;
    mainFlgComp: string;
    nkgncodes: string;
  }) => void;
  clear: () => void;
  initSourceItems: () => void;
  needsMstL: MstL[];
  needsMstM: MstM[];
  needsMstS: MstS[];
  sourceItems: SourceItem[];
  loadingMstM: boolean;
  loadingMstS: boolean;
  loadingCorp: boolean;
  disableOverseas?: boolean;
  hiddenTypeSelection?: boolean;
  hiddenMainFlag?: boolean;
  selectedmstCodeL?: string;
  selectedmstCodeM?: string;
  selectedmstCodeS?: string;
  disabledCodes?: string[];
}

interface Company {
  companyCode: string;
  companyKbn?: string;
  companyName: string;
  diffflg: string;
  stkCode: string;
  source: string;
}

interface RecentList {
  getRecent: () => Promise<void>;
  recentList: Company[];
  hiddenRecent?: boolean;
  disabledCodes?: string[];
}

interface CollectionList {
  collections: {
    binderId: string;
    caption: string;
  }[];
  corporations: Item[];
  isLoading: boolean;
  keyword: string;
  setKeyword: (keyword: string) => void;
  getBinders: (keyword?: string) => void;
  onSelectCollection: (id: string) => void;
  isBinderLoading: boolean;
  selectedId: string;
  disabledCodes?: string[];
}

interface Props {
  isOpen: boolean;
  defaultSelectedItems: Item[];
  searchList: SearchList;
  selectMenu: SelectMenu;
  recentList: RecentList;
  myCollection: CollectionList;
  vsCollection: CollectionList;
  sendAlertAtlas?: boolean;
  sendCollectionAtlas?: boolean;
  collectionMenuType?: string;
  onClose: () => void;
  onChange: (corps: Item[]) => void;
  onSelectedItemButtonClick?: () => void;
  onSwitchTab?: (item: SwitchTabItem) => void;
  alretItemCodes?: string[];
  maxSelectableNumber?: number;
}

export const CompanyModal: React.FC<Props> = ({
  isOpen,
  defaultSelectedItems,
  searchList,
  selectMenu,
  recentList,
  myCollection,
  vsCollection,
  sendAlertAtlas = false,
  sendCollectionAtlas = false,
  collectionMenuType = null,
  onClose,
  onChange,
  onSelectedItemButtonClick,
  onSwitchTab,
  alretItemCodes = [],
  maxSelectableNumber = 5000
}) => {
  const [selectedCorp, setSelectedCorp] = useState<Item[]>([]);
  const [visibleSelectedItemTable, setVisibleSelectedItemTable] = useState(
    false
  );
  const [isOpenUpperModal, setOpenUpperModal] = useState(false);
  const [visibleConfirmModal, setVisibleConfirmModal] = useState(false);

  const modalRef = useRef<React.RefObject<unknown> | null>(null);
  const ipAuthFlg = useIpAuthFlg();

  const tabs = useMemo(() => {
    const base = [
      {
        key: "corpName",
        text: "企業名から"
      },
      {
        key: "industry",
        text: "一覧から",
        onSwitch: () => selectMenu.getNeedsMstL()
      }
    ];

    if (!recentList.hiddenRecent) {
      base.push({
        key: "recent",
        text: "最近見た企業から",
        onSwitch: () => recentList.getRecent()
      });
    }

    if (ipAuthFlg) return base;

    const collectionTab = [
      {
        key: "myCollection",
        text: "マイコレクションから"
      },
      {
        key: "vsCollection",
        text: "日経コレクションから"
      }
    ];

    return [...base, ...collectionTab];
  }, [ipAuthFlg, recentList, selectMenu]);

  const [tabKey, setTabKey] = useState<string>(tabs[0].key);
  const pageInfo = findAnalyticsWithPath(location.pathname);

  useEffect(() => {
    selectMenu.initSourceItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedCorp(defaultSelectedItems);
    setTabKey(tabs[0].key);
    selectMenu.clear();
    myCollection.getBinders();
    vsCollection.getBinders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onSelect = (targets: Item[]) => {
    if (targets.length <= maxSelectableNumber) {
      setSelectedCorp(targets);
      return;
    }

    setSelectedCorp(targets.slice(0, maxSelectableNumber));
    setOpenUpperModal(true);
  };

  const onClear = () => {
    // 選択不可な企業以外は全てクリア
    setSelectedCorp(selectedCorp.filter(corp => corp.baseOptFlg === "9"));
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        title="企業選択"
        onClose={() => {
          if (!isOpen) return;

          setVisibleConfirmModal(true);
          return true;
        }}
        uncontrolled
        modalClassName={styles.modal}
        ref={modalRef}
      >
        <div
          className={styles.modalContent}
          data-testid="Modal-CompanyModal-Content"
        >
          <div>
            <SwitchTab
              items={tabs}
              selectedKey={tabKey}
              onSwitch={item => {
                if (sendCollectionAtlas && collectionMenuType) {
                  trackAction("myCollectionCorpSelectSwitchTab", {
                    tabName: item.text,
                    action:
                      collectionMenuType === "new"
                        ? "コレクション新規作成"
                        : "コレクション編集"
                  });
                }
                setTabKey(item.key);
                onSwitchTab && onSwitchTab(item);
              }}
              className={styles.tab}
            >
              <CorpName
                key={tabs[0].key}
                searchCorp={searchList.searchCorp}
                items={searchList.searchResult}
                selectedCorp={selectedCorp}
                type={searchList.type}
                maxSelectableNumber={maxSelectableNumber}
                isLoading={searchList.isLoading}
                onSelect={corp => onSelect(corp)}
                ignoreInit={searchList.ignoreInit}
                defaultKeyword={searchList.defaultKeyword}
                disabledCodes={searchList.disabledCodes}
              />
              <Industry
                key={tabs[1].key}
                mstL={selectMenu.needsMstL}
                mstM={selectMenu.needsMstM}
                mstS={selectMenu.needsMstS}
                loadingMstM={selectMenu.loadingMstM}
                loadingMstS={selectMenu.loadingMstS}
                loadingCorp={selectMenu.loadingCorp}
                corpList={selectMenu.corpList}
                selectedCorp={selectedCorp}
                onSelect={corp => onSelect(corp)}
                getMstM={selectMenu.getNeedsMstM}
                getMstS={selectMenu.getNeedsMstS}
                getCorps={selectMenu.onClickNeedsMstS}
                sourceItems={selectMenu.sourceItems}
                maxSelectableNumber={maxSelectableNumber}
                disableOverseas={selectMenu.disableOverseas}
                windowRef={modalRef}
                alretItemCodes={alretItemCodes}
                hiddenTypeSelection={selectMenu.hiddenTypeSelection}
                hiddenMainFlag={selectMenu.hiddenMainFlag}
                selectedmstCodeL={selectMenu.selectedmstCodeL}
                selectedmstCodeM={selectMenu.selectedmstCodeM}
                selectedmstCodeS={selectMenu.selectedmstCodeS}
                disabledCodes={selectMenu.disabledCodes}
              />
              {!recentList.hiddenRecent && (
                <Recent
                  key={tabs[2].key}
                  items={recentList.recentList}
                  selectedCorp={selectedCorp}
                  maxSelectableNumber={maxSelectableNumber}
                  onSelect={corp => onSelect(corp)}
                  disabledCodes={recentList.disabledCodes}
                />
              )}
              {!ipAuthFlg && (
                <Collection
                  key={!recentList.hiddenRecent ? tabs[3].key : tabs[2].key}
                  collections={myCollection.collections}
                  corporations={myCollection.corporations}
                  selectedCorp={selectedCorp}
                  maxSelectableNumber={maxSelectableNumber}
                  onSelectCollection={myCollection.onSelectCollection}
                  isLoading={myCollection.isLoading}
                  onSelectCorp={onSelect}
                  keyword={myCollection.keyword}
                  setKeyword={myCollection.setKeyword}
                  onKeywordSearch={myCollection.getBinders}
                  isBinderLoading={myCollection.isBinderLoading}
                  selectedId={myCollection.selectedId}
                  disabledCodes={myCollection.disabledCodes}
                />
              )}
              {!ipAuthFlg && (
                <Collection
                  key={!recentList.hiddenRecent ? tabs[4].key : tabs[3].key}
                  collections={vsCollection.collections}
                  corporations={vsCollection.corporations}
                  selectedCorp={selectedCorp}
                  maxSelectableNumber={maxSelectableNumber}
                  onSelectCollection={vsCollection.onSelectCollection}
                  isLoading={vsCollection.isLoading}
                  onSelectCorp={onSelect}
                  keyword={vsCollection.keyword}
                  setKeyword={vsCollection.setKeyword}
                  onKeywordSearch={vsCollection.getBinders}
                  isBinderLoading={vsCollection.isBinderLoading}
                  selectedId={vsCollection.selectedId}
                  disabledCodes={vsCollection.disabledCodes}
                />
              )}
            </SwitchTab>
            <div className={styles.selectedButton}>
              <div
                className={styles.button}
                onClick={() => {
                  if (sendCollectionAtlas && collectionMenuType) {
                    trackAction("myCollectionCorpSelectShowSelectedItems", {
                      action:
                        collectionMenuType === "new"
                          ? "コレクション新規作成"
                          : "コレクション編集"
                    });
                  }
                  setVisibleSelectedItemTable(!visibleSelectedItemTable);
                  onSelectedItemButtonClick && onSelectedItemButtonClick();
                }}
              >
                <span className={styles.icon} />
                選択済み項目を表示
              </div>
            </div>
            {visibleSelectedItemTable && (
              <SelectedItemTable
                items={selectedCorp}
                onDelete={item => {
                  setSelectedCorp(
                    selectedCorp.filter(
                      corp => corp.companyCode !== item.companyCode
                    )
                  );
                }}
                onClear={() => {
                  if (sendCollectionAtlas && collectionMenuType) {
                    trackAction("myCollectionCorpSelectReset", {
                      action:
                        collectionMenuType === "new"
                          ? "コレクション新規作成"
                          : "コレクション編集"
                    });
                  }
                  onClear();
                }}
              />
            )}
          </div>
          <div className={styles.buttonContainer}>
            <Button
              onClick={() => {
                if (sendAlertAtlas) {
                  trackAction("clickAlertConfirmAddCorpButton", {
                    tab_id: tabs.find(t => t.key === tabKey)?.text,
                    pageName: pageInfo.pageName,
                    category1: pageInfo.categories[0],
                    category2: pageInfo.categories[1],
                    category3: pageInfo.categories[2]
                  });
                }
                if (sendCollectionAtlas && collectionMenuType) {
                  trackAction("myCollectionCorpSelectSave", {
                    action:
                      collectionMenuType === "new"
                        ? "コレクション新規作成"
                        : "コレクション編集"
                  });
                }
                onChange(selectedCorp);
              }}
              className={styles.button}
              data-testid="Modal-CompanyModal-SubmitButton"
            >
              企業を設定
            </Button>
          </div>
        </div>
      </Modal>
      <UpperLimitModal
        isOpen={isOpenUpperModal}
        onClose={() => setOpenUpperModal(false)}
        maxSelectNumber={maxSelectableNumber}
      />
      <CloseConfirmModal
        isOpen={visibleConfirmModal}
        keepButtonText="入力を再開"
        onClickKeepButton={() => {
          setVisibleConfirmModal(false);
        }}
        onClickCloseButton={() => {
          setVisibleConfirmModal(false);
          onClose();
        }}
      />
    </>
  );
};
